import * as vp from 'vopay';
import { Box, Select } from 'vopay';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Button } from 'UI';

import { useCache } from '@/providers/Cache.provider';

import type { PartnerInvoiceDetailsRequestParams } from '@/hooks/useApi/useFetchPartnerInvoiceDetails';
import { useTranslation } from 'react-i18next';

type InvoicingFormProps = {
  accountId?: string;
  initValues?: { year: string; month: string };
  search: (values: PartnerInvoiceDetailsRequestParams) => void;
};

type FC = FunctionComponent<InvoicingFormProps>;

const InvoicingForm: FC = ({ initValues, search }: InvoicingFormProps) => {
  const STARTING_YEAR = 2021;
  const yearSelectOptions = useMemo(() => buildYearSelectOptions(STARTING_YEAR), []);
  const { t } = useTranslation('translations', { keyPrefix: 'Reports.List' });

  const { cache } = useCache();
  const [, cacheInvoicingFilterSettings] = cache<{ year: string; month: string }>('invoicing-form.filter');

  const [year, setYear] = useState(initYear());
  const [month, setMonth] = useState(initMonth());

  const monthOptions = Object.entries(vp.NUMERIC_MONTH_OPTIONS)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([value, label]) => ({ label, value }));

  useEffect(() => {
    cacheInvoicingFilterSettings({ year, month });
  }, [year, month]);

  const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setYear(event.currentTarget.value);
  };

  const handleMonthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMonth(event.currentTarget.value);
  };

  return (
    <Box flx="row" ai="center" gap="sm">
      <Box gap="sm">
        <Select
          version="2"
          options={monthOptions}
          placeholder={t('monthPlaceholder')}
          name="invoice-month-select"
          value={month}
          onBlur={() => {}}
          onChange={handleMonthChange}
          sort={false}
        />
        <Select
          version="2"
          options={yearSelectOptions}
          placeholder={t('yearPlaceholder')}
          name="invoice-year-select"
          value={year}
          onBlur={() => {}}
          onChange={handleYearChange}
        />
      </Box>
      <Button id="display-invoice-period" size="small" colorScheme="primary" onClick={() => search({ year, month })}>
        {t('searchButton')}
      </Button>
    </Box>
  );

  function initYear() {
    const y = initValues?.year || '';
    return yearSelectOptions.some((opt) => opt.value === y) ? y : '';
  }

  function initMonth() {
    const m = initValues?.month || '';
    return Object.prototype.hasOwnProperty.call(vp.NUMERIC_MONTH_OPTIONS, m.padStart(2, '0')) ? m.padStart(2, '0') : '';
  }

  function buildYearSelectOptions(startingYear: number) {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - startingYear + 1 }, (_, i) => {
      const year = currentYear - i;
      return { label: String(year), value: String(year) };
    });
  }
};

export default InvoicingForm;
